import React, { useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { format } from 'date-fns'

import DownloadIcon from '@mui/icons-material/Download'
import DeleteIcon from '@mui/icons-material/Delete'

import axios from '../../dataClient/dataClient'
import { theme } from '../../themes/theme'
import { getFileSize } from '../../util/strings'
import { usageTypes } from '../../util/consts'
import { handleDownloadResponse } from '../../util/downloadFile'
import { IconButton } from '../common/Button'
import { BoldSmallBodyText, ErrorText, MedBodyText } from '../styles/Text'
import DeleteFileButton from './DeleteFileButton'

const Row = styled.div`
  display: flex;
  gap: 15px;
`

const FileInfo = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 15px 15px;

  cursor: ${props => (props.clickable ? 'pointer' : 'auto')};
  :hover {
    background: ${props => (props.clickable ? `${theme.colors.primaryLight}` : 'none')};
    border-radius: ${theme.borderRadius.div};
  }

  @media only screen and (max-width: 800px) {
    flex-wrap: wrap;
    gap: 5px;
  }
`

const FileNameContainer = styled.div`
  width: ${props => (props.spacing === 'condensed' ? '100%' : '60%')};

  display: flex;
  gap: 15px;

  @media only screen and (max-width: 800px) {
    width: 100%;
    padding-right: 0;
  }
`

const Text = styled(MedBodyText)`
  font-weight: 500;

  line-height: 20px;
  text-wrap: wrap;
  word-break: break-word;

  @media only screen and (max-width: 800px) {
    width: 100%;
  }
`

const SmallText = styled(BoldSmallBodyText)`
  text-wrap: wrap;
  word-break: break-word;

  @media only screen and (max-width: 800px) {
    width: 100%;
  }
`
const SizeDateText = styled(Text)`
  width: ${props => (props.spacing === 'medium' ? 'undefined' : '20%')};

  @media only screen and (max-width: 800px) {
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    width: 100%;
  }
`

const SizeDateTextRight = styled(SizeDateText)`
  text-align: right;

  @media only screen and (max-width: 800px) {
    text-align: left;
  }
`

const ButtonColumn = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: ${props => (props.buttonAction === 'softDelete' ? 'auto' : '50px')};
`

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`

const MiniErrorText = styled(ErrorText)`
  font-size: 10px;
  text-align: right;
`
function DatabaseFile({ file, onDelete = () => {}, buttonAction = 'delete', fileClickAction = 'download', spacing = 'full', disabled = false }) {
  const [downloadError, setDownloadError] = useState(false)
  const [downloadLoading, setDownloadLoading] = useState(false)
  async function handleClick(filename, action) {
    try {
      setDownloadLoading(true)
      const response = await axios.get(`/file/${file.id}/download`, { responseType: 'blob' })
      handleDownloadResponse(response, filename, action)
    } catch (e) {
      setDownloadError(true)
      console.error('An error occurred while fetching the document', filename, e)
    } finally {
      setDownloadLoading(false)
    }
  }

  return (
    <Row>
      <FileInfo clickable onClick={() => handleClick(file.filename, fileClickAction)}>
        <FileNameContainer spacing={spacing}>
          {spacing === 'condensed' ? <SmallText>{file.filename}</SmallText> : <Text>{file.filename}</Text>}
        </FileNameContainer>
        {['full', 'medium'].includes(spacing) && <SizeDateText spacing={spacing}>{getFileSize(file.size)}</SizeDateText>}
        {spacing === 'full' && <SizeDateTextRight>{format(new Date(file.lastModified), 'MMM dd, yyyy')}</SizeDateTextRight>}
      </FileInfo>
      <ButtonColumn buttonAction={buttonAction}>
        {buttonAction === 'softDelete' && (
          <IconButton color="secondary" onClick={() => onDelete(file.id)} aria-label="Delete file" disabled={disabled}>
            <DeleteIcon />
          </IconButton>
        )}
        {buttonAction === 'delete' && <DeleteFileButton fileId={file.id} onDelete={onDelete} disabled={disabled} />}
        {buttonAction === 'download' && (
          <ErrorContainer>
            <IconButton
              aria-label={'Download file'}
              onClick={() => handleClick(file.filename, 'download')}
              loading={downloadLoading}
              disabled={disabled || downloadLoading}>
              <DownloadIcon />
            </IconButton>
            {downloadError && <MiniErrorText role="alert">Error, try again.</MiniErrorText>}
          </ErrorContainer>
        )}
      </ButtonColumn>
    </Row>
  )
}

DatabaseFile.propTypes = {
  file: PropTypes.shape({
    id: PropTypes.string.isRequired,
    filename: PropTypes.string.isRequired,
    usage: PropTypes.oneOf(usageTypes).isRequired,
    size: PropTypes.number,
    lastModified: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])
  }).isRequired,
  onDelete: PropTypes.func,
  fileClickAction: PropTypes.oneOf(['download', 'view']),
  buttonAction: PropTypes.oneOf(['delete', 'download', 'softDelete']),
  spacing: PropTypes.oneOf(['full', 'medium', 'condensed']),
  disabled: PropTypes.bool
}

export default DatabaseFile
